import { listen, qs, qsa, toggle, add, remove } from "@fluorescent/dom";

import { clearFilters, updateFilters } from "@/lib/filters/events";
import priceRange from "@/lib/filters/price-range";
import { createFocusTrap } from "focus-trap";
import debounce from "@/utils/debounce";
import { slideDown, slideUp, slideStop } from "slide-anim";

const sel = {
  bar: "[data-filter-bar]",
  filters: "[data-filter-bar-filters]",
  filterItem: "[data-filter-item]",
  dropdownToggle: "[data-dropdown-toggle]",
  group: "[data-filter-group]",
  groupLabel: "[data-filter-group-label]",
  groupValues: "[data-filter-group-values]",
  groupReset: "[data-filter-group-reset]",
  groupHeader: "[data-group-values-header]",
  priceRange: "[data-price-range]",
  rangeInput: "[data-range-input]",
  filterInput: "[data-filter-item-input]",
  sortToggle: "[data-sort-toggle]",
  sortValues: "[data-sort-values]",
  sortInput: "[data-sort-item-input]",
  resultsCount: "[data-results-count]",
  activeFilters: "[data-active-filters]",
  clearAll: "[data-clear-all-filters]",
};

const classes = {
  activeFilters: "filters-active",
  filterDisabled: "filter-item__content--disabled",
  filterBarActive: "filter-bar__filters-inner--active",
  filterBarWashActive: "filter-bar--wash-active",
  filterGroupActive: "filter-group--active",
  filterGroupRight: "filter-group__values--right",
};

// eslint-disable-next-line valid-jsdoc
/**
 * A class to handle desktop filter bar functionality
 * @param {*} node the collection section container
 * @returns renderFilters and unload methods
 */
const filterBar = node => {
  if (!node) {
    return false;
  }

  // `node` is the colelction section container.
  // Using `container` here as the filter bar container to keep filter bar
  // and filter drawer DOM scope separate.
  const container = qs(sel.bar, node);
  let groupLabels = qsa(sel.groupLabel, container);
  let rangeInputs = qsa(sel.rangeInput, container);
  let rangeContainer = qs(sel.priceRange, container);
  let focusTrap = null;
  let range = null;
  const filterDebounce = debounce();
  const events = [];

  initPriceRange(rangeContainer);
  initEvents();

  function initEvents() {
    events.push(
      listen(window, "click", clickHandler),
      listen(container, "change", changeHandler),
      listen(container, "keydown", ({ keyCode }) => {
        if (keyCode === 27) closeGroups();
      })
    );
  }

  function initPriceRange(rangeContainer) {
    if (rangeContainer) {
      range = priceRange(rangeContainer);
    }
  }

  // Elements are replaced when filters are re-rendered, so we need to get
  // up-to-date copies of these elements.
  function refreshElements() {
    groupLabels = qsa(sel.groupLabel, container);
    rangeInputs = qsa(sel.rangeInput, container);
    rangeContainer = qs(sel.priceRange, container);
  }

  // eslint-disable-next-line valid-jsdoc
  /**
   * Delegates click events
   * @param {event} e click event
   */
  function clickHandler(e) {
    const group = e.target.closest(sel.group);
    const dropdownToggle = e.target.closest(sel.dropdownToggle);
    const groupReset = e.target.closest(sel.groupReset);
    const clearAll = e.target.closest(sel.clearAll);

    // If the click happened outside of a filter group
    // We don't want to close the groups if the click happened on a filter in a group
    if (!group) {
      closeGroups();
    }

    if (dropdownToggle) {
      toggleDropdown(dropdownToggle);
    }

    if (groupReset) {
      handleGroupReset(groupReset);
    }

    if (clearAll) {
      e.preventDefault();
      clearFilters();
    }
  }

  function handleGroupReset(groupReset) {
    const group = groupReset.closest(sel.groupValues);
    const { filterType } = group.dataset;

    if (filterType === "price_range") {
      priceRangeRemove();
    } else {
      qsa(sel.filterInput, group).forEach(input => {
        input.checked = false;
      });

      updateFilters(container);
    }
  }

  function priceRangeRemove() {
    range?.reset();
    checkForActiveFilters();
    updateFilters(container);
  }

  // eslint-disable-next-line valid-jsdoc
  /**
   * Delegates change events
   * @param {event} e change event
   */
  function changeHandler(e) {
    const filterInput = e.target.closest(
      `${sel.filterInput}, ${sel.sortInput}, ${sel.rangeInput}`
    );

    if (filterInput) {
      filterChange(filterInput);
    }
  }

  function closeGroups() {
    groupLabels.forEach(button => {
      hideDropdown(button);
    });
  }

  function toggleDropdown(button) {
    const ariaExpanded = button.getAttribute("aria-expanded") === "true";
    if (ariaExpanded) {
      closeGroups();
      hideDropdown(button);
    } else {
      closeGroups();
      showDropdown(button);
    }
  }

  function showDropdown(button) {
    const group = button.closest(sel.group);
    const dropdown = qs(`#${button.getAttribute("aria-controls")}`, container);
    const { dropdownToggle } = button.dataset;

    button.setAttribute("aria-expanded", true);

    if (dropdown) {
      if (dropdownToggle === "filter-bar-filters") {
        slideStop(dropdown);
        slideDown(dropdown).then(() => {
          dropdown.setAttribute("aria-hidden", false);
        });
      } else {
        dropdown.setAttribute("aria-hidden", false);

        if (group) {
          add(group, classes.filterGroupActive);
          positionGroup(group, dropdown);

          // Lock the filter bar to stop horizontal scrolling
          add(container, classes.filterBarWashActive);
          focusTrap = createFocusTrap(group, { allowOutsideClick: true });
          focusTrap.activate();
        }
      }
    }
  }

  function hideDropdown(button) {
    const group = button.closest(sel.group);

    remove(container, classes.filterBarWashActive);
    button.setAttribute("aria-expanded", false);

    const dropdown = qs(`#${button.getAttribute("aria-controls")}`, container);
    const { dropdownToggle } = button.dataset;

    if (dropdown) {
      dropdown.setAttribute("aria-hidden", true);

      if (dropdownToggle === "filter-bar-filters") {
        slideStop(dropdown);
        slideUp(dropdown);
      } else if (group) {
        remove(group, classes.filterGroupActive);
        focusTrap && focusTrap.deactivate();
      }
    }
  }

  function positionGroup(group, dropdown) {
    remove(dropdown, classes.filterGroupRight);

    // The filter bar bounding rect
    const parentBounds = group.parentElement.getBoundingClientRect();
    // This filter groups bounding rect.
    // This will be around the toggle button
    // and what the drop down is positioned inside of
    const groupBounds = group.getBoundingClientRect();
    // The drop down bounding rect
    const dropdownBounds = dropdown.getBoundingClientRect();

    // Check if the drop down will stick out too far past the toggle button
    // Basicially checks if the drop down will overflow the page or not
    // 1. add the left side X position of the toggle button
    //    to the width of the drop down
    //    to get the left side position of the drop down
    // 2. If the left side of the drop down is past the width of the filter bar
    // 3. Add a class to the drop down to position it
    //    to the right side of the toggle button
    if (groupBounds.x + dropdownBounds.width >= parentBounds.width) {
      add(dropdown, classes.filterGroupRight);
    }
  }

  function updateGroupPositions() {
    const buttons = qsa(sel.dropdownToggle, container);

    buttons.forEach(button => {
      const ariaExpanded = button.getAttribute("aria-expanded") === "true";

      if (ariaExpanded) {
        const group = button.closest(sel.group);
        const dropdown = qs(
          `#${button.getAttribute("aria-controls")}`,
          container
        );

        if (group && dropdown) {
          positionGroup(group, dropdown);
        }
      }
    });
  }

  function filterChange(filter) {
    if (filter.classList.contains(classes.filterDisabled)) {
      return;
    }

    checkForActiveFilters();
    range?.validateRange();

    // Update results immediately for price filters and sort change
    if (
      filter.hasAttribute("data-range-input") ||
      filter.hasAttribute("data-sort-item-input")
    ) {
      updateFilters(container);
    } else {
      filterDebounce(() => updateFilters(container), 1000);
    }
  }

  function checkForActiveFilters() {
    const activeItems =
      containsCheckedInputs(qsa(sel.filterInput, container)) ||
      rangeInputsHaveValue();

    toggle(container, classes.activeFilters, activeItems);
  }

  function rangeInputsHaveValue() {
    return rangeInputs.some(input => input.value !== "");
  }

  function containsCheckedInputs(items) {
    return items.some(input => input.checked);
  }

  function renderFilters(updatedDoc) {
    const currentBar = qs(sel.bar);
    const currentBarFilters = qs(sel.filters, currentBar);
    const updatedBar = qs(sel.bar, updatedDoc);
    const updatedBarFilters = qs(sel.filters, updatedBar);
    const updatedGroupLabels = qsa(
      `${sel.groupLabel}:not(${sel.sortToggle})`,
      updatedBar
    );
    const updatedSortToggle = qs(sel.sortToggle, updatedBar);

    // Make sure the filters stay expanded when the "Clear all" button is used (they're hidden by default when no filters are applied)
    updatedBarFilters.setAttribute(
      "aria-hidden",
      currentBarFilters.getAttribute("aria-hidden")
    );
    updatedBarFilters.setAttribute(
      "style",
      currentBarFilters.getAttribute("style")
    );

    // We're going to replace the current bar with the updated content, so we need to
    // loop through the current content to maintain the state of any toggles.
    updatedGroupLabels.forEach(groupLabel => {
      const currentGroupLabel = qs(
        `[data-dropdown-toggle="${groupLabel.getAttribute(
          "data-dropdown-toggle"
        )}"]`,
        currentBar
      );

      if (currentGroupLabel) {
        const groupLabelAriaExpanded =
          currentGroupLabel.getAttribute("aria-expanded");

        if (groupLabelAriaExpanded === "true") {
          const group = groupLabel.closest(sel.group);

          group.classList.add(classes.filterGroupActive);
        }

        const currentGroupValues = qs(
          sel.groupValues,
          currentGroupLabel.parentElement
        );
        const groupValues = qs(sel.groupValues, groupLabel.parentElement);

        groupLabel.setAttribute("aria-expanded", groupLabelAriaExpanded);
        groupValues.setAttribute(
          "aria-hidden",
          currentGroupValues.getAttribute("aria-hidden")
        );
      }
    });

    if (updatedSortToggle) {
      const updatedSortValues = qs(
        sel.sortValues,
        updatedSortToggle.parentElement
      );
      const currentSortToggle = qs(sel.sortToggle, currentBar);
      const currentSortValues = qs(
        sel.sortValues,
        currentSortToggle.parentElement
      );

      updatedSortToggle.setAttribute(
        "aria-expanded",
        currentSortToggle.getAttribute("aria-expanded")
      );
      updatedSortValues.setAttribute(
        "aria-hidden",
        currentSortValues.getAttribute("aria-hidden")
      );
    }

    // We need to unload before we replace all of the content
    unload();
    currentBar.innerHTML = updatedBar.innerHTML;

    // Now that we've replaced the content, we need to re-init
    refreshElements();
    initEvents();
    initPriceRange(rangeContainer);

    updateGroupPositions();
  }

  function unload() {
    events.forEach(unsubscribe => unsubscribe());
    range?.unload();
    focusTrap?.deactivate();
  }

  return {
    renderFilters,
    unload,
  };
};

export default filterBar;
